import { useEffect, useState, Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { useSharePointFiles, SharePointFile } from '@/hooks/useSharePointFiles';
import { Download, File, FileText, Folder, Loader2, RefreshCw, Search, Trash2, Upload, Eye, AlertCircle, FolderPlus, FolderOpen, Info, Home, ChevronRight, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FilePreviewDialog } from './FilePreviewDialog';
import { MoveToFolderDialog } from './MoveToFolderDialog';

interface SharePointBrowserProps {
  projectId: string;
  folderPath?: string;
}

export function SharePointBrowser({ projectId, folderPath = '/' }: SharePointBrowserProps) {
  const { files, loading, fetchFiles, uploadMultipleFiles, deleteFile, moveFiles } = useSharePointFiles(projectId);
  const { toast } = useToast();
  const [currentPath, setCurrentPath] = useState(folderPath);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [uploadingCount, setUploadingCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [configChecked, setConfigChecked] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [projectNeedsSetup, setProjectNeedsSetup] = useState<any>(null);
  const [sharePointWebUrl, setSharePointWebUrl] = useState<string | null>(null);
  
  // Multi-select state
  const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
  
  // Preview state
  const [previewFile, setPreviewFile] = useState<SharePointFile | null>(null);
  
  // Move dialog state
  const [showMoveDialog, setShowMoveDialog] = useState(false);

  useEffect(() => {
    setCurrentPath(folderPath);
  }, [folderPath]);

  // Auto-fetch files when currentPath changes (only after config is checked)
  useEffect(() => {
    if (currentPath && !connectionError && configChecked) {
      fetchFiles(currentPath).catch((error: any) => handleFetchError(error));
    }
  }, [currentPath, configChecked]);

  // Check if project has SharePoint configured
  useEffect(() => {
    const checkProjectSharePointConfig = async () => {
      const { data: project } = await supabase
        .from('projects')
        .select('sharepoint_site_id, sharepoint_drive_id, sharepoint_folder_path, sharepoint_web_url, project_number, name')
        .eq('id', projectId)
        .single();
        
        setSharePointWebUrl(project?.sharepoint_web_url || null);
        
        if (!project?.sharepoint_site_id || !project?.sharepoint_drive_id) {
          setConnectionError('not_configured');
          setProjectNeedsSetup(project);
        }
        setConfigChecked(true);
    };
    
    checkProjectSharePointConfig();
  }, [projectId]);

  const handleFetchError = (error: any) => {
    console.error('SharePoint fetch error:', error);
    
    // Store exact error details for troubleshooting
    setErrorDetails(
      error.message || 
      error.error || 
      JSON.stringify(error, null, 2)
    );
    
    if (error.code === 'not_configured' || error.message?.includes('niet geconfigureerd')) {
      setConnectionError('not_configured');
    } else if (error.status === 404 || error.code === 'not_found') {
      setConnectionError('not_found');
    } else if (error.status === 401 || error.code === 'unauthorized') {
      setConnectionError('auth_expired');
    } else if (error.status === 403 || error.code === 'forbidden') {
      setConnectionError('forbidden');
    } else if (error.message?.includes('No Microsoft account linked')) {
      setConnectionError('sharepoint_not_connected');
    } else {
      setConnectionError('unknown');
    }
  };

  const handleCreateFolder = async () => {
    setIsCreatingFolder(true);
    try {
      if (!projectNeedsSetup) {
        const { data: project } = await supabase
          .from('projects')
          .select('project_number, name')
          .eq('id', projectId)
          .single();
        
        if (!project) {
          toast({
            title: "Fout",
            description: "Project niet gevonden",
            variant: "destructive"
          });
          return;
        }
        setProjectNeedsSetup(project);
      }

      const { data: folderData, error } = await supabase.functions.invoke('sharepoint-create-folder', {
        body: {
          projectId,
          projectNumber: projectNeedsSetup.project_number,
          projectName: projectNeedsSetup.name
        }
      });

      if (error) throw error;

      toast({
        title: "Map aangemaakt",
        description: `SharePoint map "${folderData.folderPath}" is succesvol aangemaakt en gekoppeld.`
      });

      setConnectionError(null);
      setProjectNeedsSetup(null);
      await fetchFiles(currentPath);
    } catch (error: any) {
      console.error('Error creating SharePoint folder:', error);
      toast({
        title: "Fout bij aanmaken map",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadProgress(true);
    setTotalCount(files.length);
    setUploadingCount(0);
    
    // Upload meerdere bestanden parallel
    await uploadMultipleFiles(files, currentPath, (progress) => {
      setUploadingCount(progress);
    });
    
    setUploadProgress(false);
    setUploadingCount(0);
    setTotalCount(0);
    e.target.value = '';
  };

  const handleSelectFile = (fileId: string, checked: boolean) => {
    setSelectedFileIds(prev => 
      checked ? [...prev, fileId] : prev.filter(id => id !== fileId)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedFileIds(filteredFiles.filter(f => !f.folder).map(f => f.id));
    } else {
      setSelectedFileIds([]);
    }
  };

  const handleMove = async (targetPath: string) => {
    await moveFiles(selectedFileIds, targetPath, currentPath);
    setSelectedFileIds([]);
    setShowMoveDialog(false);
  };

  const handlePreview = (file: SharePointFile) => {
    setPreviewFile(file);
  };

  const handleNavigatePreview = (direction: 'prev' | 'next') => {
    if (!previewFile) return;
    
    const nonFolderFiles = filteredFiles.filter(f => !f.folder);
    const currentIndex = nonFolderFiles.findIndex(f => f.id === previewFile.id);
    
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : nonFolderFiles.length - 1;
    } else {
      newIndex = currentIndex < nonFolderFiles.length - 1 ? currentIndex + 1 : 0;
    }
    
    setPreviewFile(nonFolderFiles[newIndex]);
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (file: SharePointFile) => {
    if (file.folder) return <Folder className="h-8 w-8 text-blue-500" />;
    
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext || '')) return <FileText className="h-8 w-8 text-red-500" />;
    if (['doc', 'docx'].includes(ext || '')) return <FileText className="h-8 w-8 text-blue-500" />;
    if (['xls', 'xlsx'].includes(ext || '')) return <FileText className="h-8 w-8 text-green-500" />;
    
    return <File className="h-8 w-8 text-gray-500" />;
  };

  const pathParts = currentPath.split('/').filter(Boolean);

  return (
    <Card>
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            SharePoint Documenten
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchFiles(currentPath)}
              disabled={loading}
              className="flex-1 sm:flex-none"
            >
              <RefreshCw className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sync</span>
            </Button>
            <label className="flex-1 sm:flex-none">
              <Button variant="default" size="sm" disabled={uploadProgress} asChild className="w-full">
                <span>
                  <Upload className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">
                    {uploadProgress && uploadingCount > 0 
                      ? `${uploadingCount}/${totalCount}...`
                      : 'Upload'}
                  </span>
                </span>
              </Button>
              <Input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileUpload}
                disabled={uploadProgress}
              />
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => sharePointWebUrl ? window.open(sharePointWebUrl, '_blank') : undefined}
              disabled={!sharePointWebUrl}
              title="Opent de projectmap in SharePoint"
              className="flex-1 sm:flex-none"
            >
              <FolderOpen className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">SharePoint</span>
            </Button>
          </div>
        </div>
        {/* Breadcrumb navigatie */}
        <div className="flex items-center gap-2 mt-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setCurrentPath('/')}
            className={currentPath === '/' ? 'bg-accent' : ''}
          >
            <Home className="h-4 w-4" />
          </Button>
          {pathParts.map((part, idx) => {
            const path = '/' + pathParts.slice(0, idx + 1).join('/');
            return (
              <Fragment key={idx}>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setCurrentPath(path)}
                  className={path === currentPath ? 'bg-accent' : ''}
                >
                  {part}
                </Button>
              </Fragment>
            );
          })}
        </div>

        <div className="flex items-center gap-2 mt-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek bestanden..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />
        </div>
      </CardHeader>
      <CardContent>
        {!connectionError && (
          <Alert className="mb-4 bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-900">💡 Tip: Bekijk bestanden ook in Teams</AlertTitle>
            <AlertDescription className="text-blue-800">
              Deze projectbestanden zijn ook beschikbaar in Microsoft Teams → Team 'Projecten' → Tab 'Alle projecten'
            </AlertDescription>
          </Alert>
        )}
        
        {connectionError === 'not_configured' && (
          <Alert className="mb-4 border-amber-500 bg-amber-50">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-900">SharePoint map is nog niet aangemaakt</AlertTitle>
            <AlertDescription className="space-y-3">
              <p className="text-amber-800">
                Voor dit project ({projectNeedsSetup?.project_number} - {projectNeedsSetup?.name}) is nog geen SharePoint map aangemaakt.
              </p>
              <Button 
                onClick={handleCreateFolder}
                disabled={isCreatingFolder}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700"
              >
                {isCreatingFolder ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Map aanmaken...
                  </>
                ) : (
                  <>
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Map nu aanmaken
                  </>
                )}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {connectionError === 'not_found' && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>SharePoint map niet gevonden</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>De SharePoint map kon niet worden gevonden.</p>
              <Button 
                onClick={handleCreateFolder} 
                disabled={isCreatingFolder}
                size="sm"
              >
                {isCreatingFolder ? 'Bezig...' : 'Map aanmaken'}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {connectionError === 'auth_expired' && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>SharePoint authenticatie verlopen</AlertTitle>
            <AlertDescription>
              Je SharePoint authenticatie is verlopen. Herverbind je account via instellingen.
            </AlertDescription>
          </Alert>
        )}

        {connectionError === 'forbidden' && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Toegang geweigerd</AlertTitle>
            <AlertDescription>
              Je hebt geen toegang tot deze SharePoint map.
            </AlertDescription>
          </Alert>
        )}

        {connectionError === 'sharepoint_not_connected' && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>SharePoint niet verbonden</AlertTitle>
            <AlertDescription>
              Verbind eerst je SharePoint account via instellingen.
            </AlertDescription>
          </Alert>
        )}

        {connectionError === 'unknown' && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>SharePoint fout</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>Er ging iets mis bij het laden van SharePoint bestanden.</p>
              {errorDetails && (
                <p className="text-xs font-mono bg-muted/50 p-2 rounded">
                  {errorDetails}
                </p>
              )}
              <Button onClick={() => fetchFiles(currentPath)} size="sm" variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Probeer opnieuw
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Multi-select toolbar */}
        {selectedFileIds.length > 0 && (
          <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">{selectedFileIds.length} geselecteerd</span>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowMoveDialog(true)}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Verplaats naar...
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedFileIds([])}
              >
                Annuleer selectie
              </Button>
            </div>
          </div>
        )}

        {loading && files.length === 0 ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredFiles.length === 0 && !connectionError ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? 'Geen bestanden gevonden' : 'Nog geen documenten in dit project'}
          </div>
        ) : (
          <div className="space-y-2">
            {/* Select all checkbox */}
            {filteredFiles.some(f => !f.folder) && (
              <div className="flex items-center gap-2 pb-2 border-b">
                <Checkbox 
                  checked={selectedFileIds.length === filteredFiles.filter(f => !f.folder).length && selectedFileIds.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm text-muted-foreground">Selecteer alles</span>
              </div>
            )}

            <div className="grid gap-2">
              {filteredFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {!file.folder && (
                      <Checkbox 
                        checked={selectedFileIds.includes(file.id)}
                        onCheckedChange={(checked) => handleSelectFile(file.id, checked as boolean)}
                      />
                    )}
                    {getFileIcon(file)}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {!file.folder && formatFileSize(file.size)} •{' '}
                        {formatDistanceToNow(new Date(file.lastModifiedDateTime), {
                          addSuffix: true,
                          locale: nl
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {file.folder ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newPath = `${currentPath.replace(/\/+$/, '')}/${file.name}`;
                          setCurrentPath(newPath);
                        }}
                      >
                        <Folder className="h-4 w-4" />
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreview(file)}
                          title="Preview"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {file.downloadUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(file.downloadUrl, '_blank')}
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(file.id)}
                      title="Verwijder"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bestand verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je dit bestand wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) {
                  deleteFile(deleteId, currentPath);
                  setDeleteId(null);
                }
              }}
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview dialog */}
      <FilePreviewDialog 
        file={previewFile}
        files={filteredFiles.filter(f => !f.folder)}
        onClose={() => setPreviewFile(null)}
        onNavigate={handleNavigatePreview}
      />

      {/* Move dialog */}
      <MoveToFolderDialog 
        open={showMoveDialog}
        onClose={() => setShowMoveDialog(false)}
        onMove={handleMove}
        currentPath={currentPath}
        files={files}
      />
    </Card>
  );
}